import { Link, useNavigate } from 'react-router-dom'
import { X, Users, User, HelpCircle, LogOut, Shield, LucideIcon } from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'

export interface MoreItem {
  icon: LucideIcon
  label: string
  href?: string
  onClick?: () => void
  danger?: boolean
}

export interface MoreBottomSheetProps {
  isOpen: boolean
  onClose: () => void
  userRole?: string
}

const userMoreItems: MoreItem[] = [
  {
    icon: Users,
    label: 'My Supporter',
    href: '/u/supporter'
  },
  {
    icon: User,
    label: 'Account',
    href: '/u/account'
  },
  {
    icon: HelpCircle,
    label: 'Help & Support',
    href: '/help'
  }
]

const supporterMoreItems: MoreItem[] = [
  {
    icon: User,
    label: 'Account',
    href: '/u/account'
  },
  {
    icon: HelpCircle,
    label: 'Help & Support',
    href: '/help'
  }
]

const adminMoreItems: MoreItem[] = [
  {
    icon: Shield,
    label: 'Admin Dashboard',
    href: '/admin'
  },
  {
    icon: User,
    label: 'Account',
    href: '/u/account'
  },
  {
    icon: HelpCircle,
    label: 'Help & Support',
    href: '/help'
  }
]

export function MoreBottomSheet({ isOpen, onClose, userRole = 'user' }: MoreBottomSheetProps) {
  const navigate = useNavigate()
  const { signOut } = useAuthStore()

  const handleSignOut = async () => {
    await signOut()
    navigate('/auth/sign-in')
    onClose()
  }

  let moreItems: MoreItem[] = userMoreItems
  if (userRole === 'supporter') {
    moreItems = supporterMoreItems
  } else if (userRole === 'admin') {
    moreItems = adminMoreItems
  }

  // Add log out at the end
  const allItems: MoreItem[] = [
    ...moreItems,
    {
      icon: LogOut,
      label: 'Log Out',
      onClick: handleSignOut,
      danger: true
    }
  ]

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-50 transition-opacity"
        onClick={onClose}
      />

      {/* Sheet */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-2xl shadow-xl animate-slide-up">
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-10 h-1 bg-[#A4A9B3] rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-4 pb-4 border-b border-[#E3DED2]">
          <h2 className="text-lg font-semibold text-[#0F1C2E]">More</h2>
          <button
            onClick={onClose}
            className="p-2 text-[#6E7582] hover:text-[#0F1C2E] hover:bg-[#F6F3EC] rounded-lg transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Menu items */}
        <nav className="p-2" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 0.5rem)' }}>
          {allItems.map((item) => {
            const Icon = item.icon

            if (item.href) {
              return (
                <Link
                  key={item.label}
                  to={item.href}
                  onClick={onClose}
                  className={`
                    flex items-center gap-4 px-4 py-3 rounded-lg transition-colors
                    ${item.danger
                      ? 'text-[#C7796B] hover:bg-[#C7796B]/8'
                      : 'text-[#0F1C2E] hover:bg-[#F6F3EC]'
                    }
                  `}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </Link>
              )
            }

            return (
              <button
                key={item.label}
                onClick={item.onClick}
                className={`
                  flex items-center gap-4 w-full px-4 py-3 rounded-lg transition-colors
                  ${item.danger
                    ? 'text-[#C7796B] hover:bg-[#C7796B]/8'
                    : 'text-[#0F1C2E] hover:bg-[#F6F3EC]'
                  }
                `}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </button>
            )
          })}
        </nav>
      </div>

      {/* Animation styles */}
      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </>
  )
}
